import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { apiConfig } from "../../../utils/apiConfig";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import styled from "styled-components";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import SubPageLayout from "../../../components/shared/SubPageLayout";

import logger from '../../../utils/logger';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const TableContainer = styled.div`
  margin-top: 20px;
  margin-bottom: 20px;
  width: 100%;
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;

  th,
  td {
    border: 1px solid ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[700] : theme.colors.neutral[200]};
    padding: 8px;
    text-align: right;
    color: ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[100] : theme.colors.neutral[900]};
  }

  th {
    background: ${({ theme }) => theme.isDarkMode ? theme.colors.surface.default : theme.colors.surface.paper};
    font-weight: bold;
  }

  tr:nth-child(even) {
    background: ${({ theme }) => theme.isDarkMode ? theme.colors.surface.default : theme.colors.surface.paper};
  }
`;

const ChartContainer = styled.div`
  margin-top: 20px;
  margin-bottom: 20px;
  width: 100%;
  height: 600px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 16px;
  margin: 40px 0;
  justify-content: center;
`;

const ActionButton = styled.button`
  background: ${({ theme }) => theme.isDarkMode ? theme.colors.surface.default : theme.colors.surface.paper};
  color: white;
  font-weight: bold;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  transition: background-color 0.2s;

  &:hover {
    background: ${({ theme }) => theme.isDarkMode ? theme.colors.surface.default : theme.colors.surface.paper};
  }

  &:disabled {
    background: ${({ theme }) => theme.isDarkMode ? theme.colors.surface.default : theme.colors.surface.paper};
    cursor: not-allowed;
  }
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
  background: ${({ theme }) => theme.isDarkMode ? theme.colors.surface.default : theme.colors.surface.paper};
  height: calc(100vh - 64px); // Adjust this value based on your header height
  padding: 20px;
`;

const ContentWrapper = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  width: 100%;
`;

const ResultsContainer = styled.div`
  background: ${({ theme }) => theme.isDarkMode ? theme.colors.surface.default : theme.colors.surface.paper};
  border-radius: 0.5rem;
  box-shadow: ${({ theme }) => theme.isDarkMode ? "none" : theme.shadows.md};
  padding: 2rem;
  margin-bottom: 2rem;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const HeaderButton = styled(Link)`
  background: ${({ theme }) => theme.isDarkMode ? theme.colors.surface.default : theme.colors.surface.paper};
  color: white;
  font-weight: bold;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  transition: background-color 0.2s;
  text-decoration: none;
  display: inline-block;

  &:hover {
    background: ${({ theme }) => theme.isDarkMode ? theme.colors.surface.default : theme.colors.surface.paper};
  }
`;

const CenteredTitle = styled.h1`
  text-align: center;
  font-size: 1.875rem;
  font-weight: bold;
  color: ${({ theme }) => theme.isDarkMode ? theme.colors.neutral[100] : theme.colors.neutral[900]};
`;

const RMDAnalysis = () => {
  const { householdId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    birthDate: "",
    accountBalance: "",
    spouseBeneficiary: "No",
    spouseBirthDate: "",
    maxAge: 90,
    growthRate: "6.00%",
    inflationRate: 2.5,
    filingStatus: "single",
  });
  const [projections, setProjections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const chartRef = React.useRef(null);
  const tableRef = React.useRef(null);
  const [rmdTables, setRmdTables] = useState(null);
  const [taxData, setTaxData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const calculateRMD = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!rmdTables) {
        throw new Error("RMD tables not loaded");
      }

      const { UNIFORM_LIFETIME_TABLE } = rmdTables;

      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }

      // Format the numeric values
      const formattedData = {
        ...formData,
        accountBalance: parseFloat(
          formData.accountBalance.replace(/[^0-9.]/g, ""),
        ),
        growthRate: parseFloat(formData.growthRate.replace(/[^0-9.]/g, "")),
        inflationRate: parseFloat(formData.inflationRate),
        maxAge: parseInt(formData.maxAge),
        householdId,
      };

      logger.info("Sending request with data:", formattedData);

      const response = await apiConfig.api.post(
        "/advisor/financial-planning/rmd",
        formattedData,
      );

      logger.info("Received response:", response.data);
      setProjections(response.data.projections);
    } catch (err) {
      logger.error("Error calculating RMD:", err);
      setError(err.message || "Error calculating RMD");
    } finally {
      setLoading(false);
    }
  };

  const calculateLifetimeTotals = (projections) => {
    const grossDistributions = projections.reduce(
      (sum, p) => sum + (p.rmd || 0),
      0,
    );
    const projectedTaxes = projections.reduce(
      (sum, p) => sum + (p.tax || 0),
      0,
    );
    const netDistributions = grossDistributions - projectedTaxes;
    return { grossDistributions, projectedTaxes, netDistributions };
  };

  const totalsPlugin = {
    id: "totals",
    afterDraw: (chart, args, options) => {
      const {
        ctx,
        chartArea: { top, right },
      } = chart;
      const totals = calculateLifetimeTotals(projections);

      ctx.save();
      ctx.fillStyle = "rgba(255, 255, 255, 0.8)";
      ctx.fillRect(right - 220, top + 10, 210, 100);

      ctx.fillStyle = "black";
      ctx.font = "bold 12px Arial";
      ctx.textAlign = "left";
      ctx.fillText("Lifetime Totals", right - 210, top + 30);

      ctx.font = "11px Arial";
      ctx.fillText(
        `Gross Distributions: $${totals.grossDistributions.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
        right - 210,
        top + 50,
      );
      ctx.fillText(
        `Projected Taxes: $${totals.projectedTaxes.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
        right - 210,
        top + 70,
      );
      ctx.fillText(
        `Net Distributions: $${totals.netDistributions.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
        right - 210,
        top + 90,
      );

      ctx.restore();
    },
  };

  const calculateMaxValue = (projections) => {
    const maxDataValue = Math.max(
      ...projections.map((p) => p.accountBalance),
      ...projections.map((p) => p.rmd + p.tax),
    );
    const extendedMaxValue = Math.ceil(maxDataValue * 1.33);

    const getRoundingFactor = (value) => {
      const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
      if (value / magnitude < 2) return magnitude / 5;
      if (value / magnitude < 5) return magnitude / 2;
      return magnitude;
    };

    const roundingFactor = getRoundingFactor(extendedMaxValue);
    return Math.ceil(extendedMaxValue / roundingFactor) * roundingFactor;
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: "RMD Projections",
        color: "#000000",
        font: {
          size: 16,
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            if (context.parsed.y !== null) {
              label += "$" + context.parsed.y.toLocaleString();
            }
            return label;
          },
        },
      },
      legend: {
        position: "bottom",
        labels: {
          color: "#000000",
          font: {
            size: 12,
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        title: {
          display: true,
          text: "Age",
          color: "#000000",
          font: {
            size: 14,
          },
        },
        ticks: {
          color: "#000000",
          font: {
            size: 12,
          },
        },
      },
      y: {
        stacked: true,
        title: {
          display: true,
          text: "Amount ($)",
          color: "#000000",
          font: {
            size: 14,
          },
        },
        ticks: {
          color: "#000000",
          callback: (value) => "$" + value.toLocaleString(),
          font: {
            size: 12,
          },
        },
        max: projections ? calculateMaxValue(projections) : undefined,
      },
    },
  };

  const handleSaveToHousehold = async () => {
    setIsSaving(true);
    try {
      const response = await apiConfig.api.post(
        `/advisor-portal/client-database/household-profile/${householdId}/calculators/rmd`,
        {
          projections,
          formData,
        },
      );

      if (response.status === 200) {
        alert("RMD analysis saved successfully");
      }
    } catch (error) {
      logger.error("Error saving RMD analysis:", error);
      alert("Failed to save RMD analysis");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadReport = async () => {
    setIsDownloading(true);
    try {
      // Create PDF document
      const pdf = new jsPDF("p", "pt", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();

      // Add title
      pdf.setFontSize(20);
      pdf.text("RMD Analysis Report", pageWidth / 2, 40, { align: "center" });

      // Add table
      const tableCanvas = await html2canvas(tableRef.current);
      const tableImage = tableCanvas.toDataURL("image/png");
      const tableAspectRatio = tableCanvas.height / tableCanvas.width;
      const tableWidth = pageWidth - 80; // 40px margin on each side
      const tableHeight = tableWidth * tableAspectRatio;
      pdf.addImage(tableImage, "PNG", 40, 60, tableWidth, tableHeight);

      // Add chart on new page
      pdf.addPage();
      const chartCanvas = await html2canvas(chartRef.current);
      const chartImage = chartCanvas.toDataURL("image/png");
      const chartAspectRatio = chartCanvas.height / chartCanvas.width;
      const chartWidth = pageWidth - 80;
      const chartHeight = chartWidth * chartAspectRatio;
      pdf.addImage(chartImage, "PNG", 40, 40, chartWidth, chartHeight);

      // Download PDF
      pdf.save("RMD_Analysis.pdf");
    } catch (error) {
      logger.error("Error generating PDF:", error);
      alert("Failed to generate PDF");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleOpenInNewTab = (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    if (!token) {
      alert("Please log in to continue");
      return;
    }
    window.open(
      `${window.location.origin}/advisor-portal/household-profile/${householdId}/rmd-analysis`,
      "_blank",
    );
  };

  const handleBackToHousehold = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      alert("Please log in to continue");
      return;
    }
    // Ensure token is in headers for any subsequent requests
    apiConfig.api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    navigate(`/advisor/household/${householdId}`);
  };

  // Add useEffect to handle token on component mount
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      apiConfig.api.defaults.headers.common["Authorization"] =
        `Bearer ${token}`;
    }

    // Check if this is a new tab opened from another RMD calculator
    const tempToken = sessionStorage.getItem("temp_token");
    if (tempToken) {
      localStorage.setItem("token", tempToken);
      sessionStorage.removeItem("temp_token");
      apiConfig.api.defaults.headers.common["Authorization"] =
        `Bearer ${tempToken}`;
    }
  }, []);

  // Replace useTaxAndRmdData hook with useEffect
  useEffect(() => {
    Promise.all([
      fetch("/data/rmdTables.json").then((res) => res.json()),
      fetch("/data/taxData.json").then((res) => res.json()),
    ])
      .then(([rmdData, taxData]) => {
        setRmdTables(rmdData);
        setTaxData(taxData);
        setLoading(false);
      })
      .catch((error) => {
        logger.error("Error loading data:", error);
        setError(error.message);
        setLoading(false);
      });
  }, []);

  const formatAsCurrency = (value) => {
    try {
      const numericValue =
        parseFloat(value?.toString().replace(/[$,]/g, "")) || 0;
      return `$${numericValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
    } catch (err) {
      logger.error("Error formatting currency:", err);
      return "$0.00";
    }
  };

  const formatAsPercentage = (value) => {
    try {
      const numericValue =
        parseFloat(value?.toString().replace(/[^0-9.]/g, "")) || 0;
      return `${numericValue.toFixed(2)}%`;
    } catch (err) {
      logger.error("Error formatting percentage:", err);
      return "0.00%";
    }
  };

  if (loading) {
    return (
      <SubPageLayout
        title="Loading..."
        breadcrumbItems={[
          { label: "Client Database", path: "/advisor-portal/clients" },
          {
            label: "Household Profile",
            path: `/advisor-portal/household-profile/${householdId}`,
          },
          {
            label: "RMD Analysis",
            path: `/advisor-portal/household-profile/${householdId}/rmd-analysis`,
          },
        ]}
      >
        <div className="text-gray-700 dark:text-gray-300">Loading...</div>
      </SubPageLayout>
    );
  }

  if (error) {
    return (
      <SubPageLayout
        title="Error"
        breadcrumbItems={[
          { label: "Client Database", path: "/advisor-portal/clients" },
          {
            label: "Household Profile",
            path: `/advisor-portal/household-profile/${householdId}`,
          },
          {
            label: "RMD Analysis",
            path: `/advisor-portal/household-profile/${householdId}/rmd-analysis`,
          },
        ]}
      >
        <div className="text-red-500">Error: {error}</div>
      </SubPageLayout>
    );
  }

  return (
    <SubPageLayout
      title="RMD Calculator"
      subtitle="Calculate and analyze Required Minimum Distributions"
      breadcrumbItems={[
        { label: "Client Database", path: "/advisor-portal/clients" },
        {
          label: "Household Profile",
          path: `/advisor-portal/household-profile/${householdId}`,
        },
        {
          label: "RMD Analysis",
          path: `/advisor-portal/household-profile/${householdId}/rmd-analysis`,
        },
      ]}
      actions={
        <button
          onClick={handleOpenInNewTab}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
        >
          Open in New Tab
        </button>
      }
    >
      <div className="bg-surface-default dark:bg-surface-default shadow rounded p-6">
        <form onSubmit={calculateRMD} className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Birth Date */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
                Your date of birth:
              </label>
              <input
                type="date"
                name="birthDate"
                value={formData.birthDate}
                onChange={handleInputChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 leading-tight focus:outline-none focus:shadow-outline"
                required
              />
            </div>

            {/* Account Balance - Updated */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
                Account balance (as of 12/31 of last year):
              </label>
              <input
                type="text"
                name="accountBalance"
                value={formData.accountBalance}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, "");
                  handleInputChange({
                    target: {
                      name: "accountBalance",
                      value,
                    },
                  });
                }}
                onBlur={(e) => {
                  handleInputChange({
                    target: {
                      name: "accountBalance",
                      value: formatAsCurrency(e.target.value),
                    },
                  });
                }}
                onFocus={(e) => {
                  if (e.target.value === "$0.00") {
                    handleInputChange({
                      target: {
                        name: "accountBalance",
                        value: "",
                      },
                    });
                  }
                }}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="$0.00"
                required
              />
            </div>

            {/* Spouse Beneficiary */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
                Is your spouse the primary beneficiary?
              </label>
              <select
                name="spouseBeneficiary"
                value={formData.spouseBeneficiary}
                onChange={handleInputChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 leading-tight focus:outline-none focus:shadow-outline"
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            {/* Spouse Birth Date */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
                Spouse's date of birth:
              </label>
              <input
                type="date"
                name="spouseBirthDate"
                value={formData.spouseBirthDate}
                onChange={handleInputChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 leading-tight focus:outline-none focus:shadow-outline"
                disabled={formData.spouseBeneficiary === "No"}
              />
            </div>

            {/* Filing Status */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
                Tax Filing Status:
              </label>
              <select
                name="filingStatus"
                value={formData.filingStatus}
                onChange={handleInputChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 leading-tight focus:outline-none focus:shadow-outline"
              >
                <option value="single">Single</option>
                <option value="married">Married Filing Jointly</option>
                <option value="head">Head of Household</option>
              </select>
            </div>

            {/* Max Age */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
                Maximum age for calculations (up to 120):
              </label>
              <input
                type="number"
                name="maxAge"
                value={formData.maxAge}
                onChange={handleInputChange}
                min="90"
                max="120"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>

            {/* Growth Rate - Updated */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
                Expected annual growth rate (%):
              </label>
              <input
                type="text"
                name="growthRate"
                value={formData.growthRate}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, "");
                  handleInputChange({
                    target: {
                      name: "growthRate",
                      value,
                    },
                  });
                }}
                onBlur={(e) => {
                  handleInputChange({
                    target: {
                      name: "growthRate",
                      value: formatAsPercentage(e.target.value),
                    },
                  });
                }}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 leading-tight focus:outline-none focus:shadow-outline"
                placeholder="0.00%"
              />
            </div>

            {/* Inflation Rate */}
            <div>
              <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
                Estimated annual inflation rate (%):
              </label>
              <select
                name="inflationRate"
                value={formData.inflationRate}
                onChange={handleInputChange}
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 dark:text-gray-300 leading-tight focus:outline-none focus:shadow-outline"
              >
                <option value="1.0">1.0%</option>
                <option value="1.5">1.5%</option>
                <option value="2.0">2.0%</option>
                <option value="2.5">2.5%</option>
                <option value="3.0">3.0%</option>
                <option value="3.5">3.5%</option>
              </select>
            </div>
          </div>

          <div className="mt-6">
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              disabled={loading}
            >
              {loading ? "Calculating..." : "Calculate"}
            </button>
          </div>
        </form>

        {projections && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              RMD Projections
            </h2>
            <TableContainer ref={tableRef}>
              <StyledTable>
                <thead>
                  <tr>
                    <th className="py-2 px-4 bg-gray-100 font-bold uppercase text-sm text-gray-600 dark:text-gray-400 border-b">
                      Year
                    </th>
                    <th className="py-2 px-4 bg-gray-100 font-bold uppercase text-sm text-gray-600 dark:text-gray-400 border-b">
                      Age
                    </th>
                    <th className="py-2 px-4 bg-gray-100 font-bold uppercase text-sm text-gray-600 dark:text-gray-400 border-b">
                      Account Balance
                    </th>
                    <th className="py-2 px-4 bg-gray-100 font-bold uppercase text-sm text-gray-600 dark:text-gray-400 border-b">
                      RMD
                    </th>
                    <th className="py-2 px-4 bg-gray-100 font-bold uppercase text-sm text-gray-600 dark:text-gray-400 border-b">
                      Tax
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {projections.map((projection, index) => (
                    <tr key={index}>
                      <td className="py-2 px-4 border-b">
                        {projection.year || "-"}
                      </td>
                      <td className="py-2 px-4 border-b">
                        {projection.age || "-"}
                      </td>
                      <td className="py-2 px-4 border-b">
                        $
                        {(projection.accountBalance || 0).toLocaleString(
                          undefined,
                          { minimumFractionDigits: 2 },
                        )}
                      </td>
                      <td className="py-2 px-4 border-b">
                        $
                        {(projection.rmd || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </td>
                      <td className="py-2 px-4 border-b">
                        $
                        {(projection.tax || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </TableContainer>

            <ChartContainer ref={chartRef}>
              <Bar
                data={{
                  labels: projections.map((p) => p.age || 0),
                  datasets: [
                    {
                      label: "Account Balance",
                      data: projections.map((p) => p.accountBalance || 0),
                      backgroundColor: "rgba(54, 162, 235, 0.8)",
                      borderColor: "rgba(54, 162, 235, 1)",
                      borderWidth: 1,
                      stack: "stack1",
                    },
                    {
                      label: "RMD Distribution",
                      data: projections.map((p) => p.rmd || 0),
                      backgroundColor: "rgba(75, 192, 192, 0.8)",
                      borderColor: "rgba(75, 192, 192, 1)",
                      borderWidth: 1,
                      stack: "stack1",
                    },
                    {
                      label: "Taxes",
                      data: projections.map((p) => p.tax || 0),
                      backgroundColor: "rgba(255, 99, 132, 0.8)",
                      borderColor: "rgba(255, 99, 132, 1)",
                      borderWidth: 1,
                      stack: "stack1",
                    },
                  ],
                }}
                options={{
                  ...chartOptions,
                  scales: {
                    ...chartOptions.scales,
                    x: {
                      ...chartOptions.scales.x,
                      stacked: true,
                    },
                    y: {
                      ...chartOptions.scales.y,
                      stacked: true,
                    },
                  },
                }}
                plugins={[totalsPlugin]}
              />
            </ChartContainer>

            <ButtonContainer>
              <ActionButton onClick={handleSaveToHousehold} disabled={isSaving}>
                {isSaving ? "Saving..." : "Save to Household Documents"}
              </ActionButton>
              <ActionButton
                onClick={handleDownloadReport}
                disabled={isDownloading}
              >
                {isDownloading ? "Generating PDF..." : "Download Report"}
              </ActionButton>
            </ButtonContainer>
          </div>
        )}
      </div>
    </SubPageLayout>
  );
};

export default RMDAnalysis;
